import { Grid, Select, Textarea, TextInput } from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { WAREHOUSE_TYPE } from "constants/common/warehouse";
import dayjs from "configs/dayjs";
import { FormikProps, getIn } from "formik";
import { memo, useEffect } from "react";
import { useListWarehouse } from "utils/hooks/request/useWarehouses";
import { SOCreateFormValues } from "../../config";

const spanHalf = { base: 12, md: 6 };

interface SalesOrderGeneralFieldsProps {
  formik: FormikProps<SOCreateFormValues>;
}

const SalesOrderGeneralFields = memo(
  ({ formik }: SalesOrderGeneralFieldsProps) => {
    const { warehouses, warehouseOptions } = useListWarehouse();

    useEffect(() => {
      if (formik.values.warehouseId) return;
      const mainWarehouse = warehouses?.find(
        (warehouse) => warehouse.type === WAREHOUSE_TYPE.MAIN,
      );
      if (mainWarehouse) {
        formik.setFieldValue("warehouseId", mainWarehouse.id);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [warehouses]);

    const getFieldError = (field: keyof SOCreateFormValues) => {
      const touched = getIn(formik.touched, field);
      const error = getIn(formik.errors, field);
      return touched && error ? (error as string) : null;
    };

    return (
      <Grid>
        <Grid.Col span={spanHalf}>
          <TextInput
            label="Mã đơn hàng"
            placeholder="Để trống để tự động tạo"
            {...formik.getFieldProps("code")}
            error={getFieldError("code")}
          />
        </Grid.Col>

        <Grid.Col span={spanHalf}>
          <DateTimePicker
            label="Ngày tạo"
            placeholder="Chọn ngày tạo"
            withAsterisk
            value={
              formik.values.createdAt
                ? dayjs.unix(formik.values.createdAt).toDate()
                : null
            }
            onChange={(value) =>
              formik.setFieldValue(
                "createdAt",
                value ? dayjs(value).unix() : undefined,
              )
            }
            onBlur={() => formik.setFieldTouched("createdAt", true)}
            error={getFieldError("createdAt")}
          />
        </Grid.Col>

        <Grid.Col span={spanHalf}>
          <Select
            label="Kho xuất"
            placeholder="Chọn kho"
            withAsterisk
            data={warehouseOptions ?? []}
            value={formik.values.warehouseId}
            onChange={(value) => formik.setFieldValue("warehouseId", value)}
            onBlur={() => formik.setFieldTouched("warehouseId", true)}
            error={getFieldError("warehouseId")}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <Textarea
            label="Ghi chú"
            placeholder="Nhập ghi chú (tuỳ chọn)"
            autosize
            minRows={2}
            {...formik.getFieldProps("notes")}
            error={getFieldError("notes")}
          />
        </Grid.Col>
      </Grid>
    );
  },
  (prev, next) =>
    prev.formik.values.code === next.formik.values.code &&
    prev.formik.values.warehouseId === next.formik.values.warehouseId &&
    prev.formik.values.notes === next.formik.values.notes &&
    prev.formik.values.createdAt === next.formik.values.createdAt &&
    prev.formik.errors.code === next.formik.errors.code &&
    prev.formik.errors.warehouseId === next.formik.errors.warehouseId &&
    prev.formik.errors.notes === next.formik.errors.notes &&
    prev.formik.errors.createdAt === next.formik.errors.createdAt &&
    prev.formik.touched.code === next.formik.touched.code &&
    prev.formik.touched.warehouseId === next.formik.touched.warehouseId &&
    prev.formik.touched.notes === next.formik.touched.notes &&
    prev.formik.touched.createdAt === next.formik.touched.createdAt,
);

SalesOrderGeneralFields.displayName = "SalesOrderGeneralFields";

export default SalesOrderGeneralFields;
